import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Shield, Package, FileText, Megaphone, Zap } from 'lucide-react';

export default function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err.message || 'Sign in failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Package, text: 'Reorder supplies & consumables instantly' },
    { icon: Megaphone, text: 'Access branded marketing materials' },
    { icon: FileText, text: 'Download clinical forms & documents' },
    { icon: Zap, text: 'Place custom device orders with ease' },
  ];

  return (
    <div className="login-page">
      {/* LEFT PANEL */}
      <div className="login-left">
        {!logoError ? (
          <img
            src="https://neurogenx.com/wp-content/uploads/2022/01/Neurogenx-Logo.png"
            alt="Neurogenx"
            className="login-brand-logo"
            onError={() => setLogoError(true)}
          />
        ) : (
          <div style={{ fontSize: 28, fontWeight: 700, color: 'white', marginBottom: 48 }}>
            NEUROGENX
          </div>
        )}

        <h1 className="login-headline">
          Preferred Provider<br />
          <span>Portal</span>
        </h1>
        <p className="login-subtext">
          Your dedicated hub for managing supplies, resources, and practice materials — all in one place.
        </p>

        <div className="login-features">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="login-feature">
                <div className="login-feature-icon"><Icon size={14} /></div>
                {f.text}
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="login-right">
        <div className="login-form-box">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: '#10497c', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Shield size={18} color="white" />
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Secure Provider Access</div>
          </div>

          <h2 className="login-form-title">Sign In</h2>
          <p className="login-form-sub">
            Access your Neurogenx provider portal.<br />
            Not yet a partner? <a href="https://neurogenx.com/contact" target="_blank" rel="noopener noreferrer">Apply here →</a>
          </p>

          {error && (
            <div className="alert alert-error mb-16">
              <Shield size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group mb-16">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className="form-input"
                placeholder="you@practice.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group mb-8">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  style={{ paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8',
                    display: 'flex', alignItems: 'center'
                  }}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="login-forgot mb-16">
              <a href="#forgot">Forgot password?</a>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full btn-lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner" style={{ width: 16, height: 16, borderWidth: 2, margin: 0 }} />
                  Signing in...
                </>
              ) : 'Sign In to Portal'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: '#94a3b8' }}>
            Protected by Supabase Auth · Encrypted in transit
          </p>
        </div>
      </div>
    </div>
  );
}
